import DOMPurify from "dompurify";
import parse from "html-react-parser";
import { Base64 } from "js-base64";
import linkifyHtml from "linkify-html";
import queryString from "query-string";

import { Backend, absoluteUrl, build_api_url, getAuthHost, get_csrf_jwt_token } from "@vkplay/shared";

export const BackendPromise = {
    get(url, opts) {
        return new Promise((resolve, reject) => {
            Backend.get(url, opts).then(makeAPIResponseHandler(resolve, reject)).catch(catchErrorHandler(reject));
        });
    },
    post(url, opts) {
        return new Promise((resolve, reject) => {
            Backend.post(url, opts).then(makeAPIResponseHandler(resolve, reject)).catch(catchErrorHandler(reject));
        });
    },
    post_real_formdata(url, formdata) {
        return new Promise((resolve, reject) => {
            Backend.post_real_formdata(url, formdata)
                .then(makeAPIResponseHandler(resolve, reject))
                .catch(catchErrorHandler(reject));
        });
    },
    delete(url) {
        return new Promise((resolve, reject) => {
            // Backend.delete по каким то причинам возвращает строку, а не JSON
            Backend.delete(url)
                .then(makeAPIResponseHandler(resolve, reject, true))
                .catch(catchErrorHandler(reject));
        });
    },
    ext_api_get(url, opts) {
        return new Promise((resolve, reject) => {
            Backend.api_get(url, opts).then(makeAPIResponseHandler(resolve, reject)).catch(catchErrorHandler(reject));
        });
    },
    ext_api_post(url, opts) {
        return new Promise((resolve, reject) => {
            Backend.api_post(url, opts).then(makeAPIResponseHandler(resolve, reject)).catch(catchErrorHandler(reject));
        });
    },
    ext_api_delete(url, fetch_params) {
        return get_csrf_jwt_token().then(() => {
            url = build_api_url(url);
            return new Promise((resolve, reject) => {
                fetch(url, {
                    method: "DELETE",
                    ...fetch_params,
                })
                    .then(makeAPIResponseHandler(resolve, reject))
                    .catch(catchErrorHandler(reject));
            });
        });
    },
};

function makeAPIResponseHandler(resolve, reject, json_parse = false) {
    return (resp) => {
        if (json_parse && is_str(resp)) {
            resp = JSON.parse(resp);
        }
        if (resp) {
            if (resp.status === "fail" && resp.errmsg) {
                alert("Error on developers.vkplay.ru\n" + resp.errmsg);
            } else if (resp.code === "no_sdc") {
                // Ставим sdc куку через редирект
                window.location.replace(
                    `https://${getAuthHost("auth-ac")}/sdc?from=${absoluteUrl(window.location.href)}`
                );
            }
            if (resp.alert_msg) {
                alert(resp.alert_msg);
            }
            resolve(resp);
        } else {
            reject();
        }
    };
}

function catchErrorHandler(reject) {
    return (error) => {
        if (error.status) {
            alert("Error on developers.vkplay.ru\n" + error.status);
        }
        reject(error);
    };
}

function capitalize(s) {
    if (!is_str(s)) return "";
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function prepareQSVal(val) {
    if (typeof val == "boolean") {
        val = capitalize(val.toString());
    }
    return encodeURIComponent(val);
}

export function buildQS(obj) {
    return Object.keys(obj)
        .filter((key) => obj[key] !== undefined)
        .map((key) => {
            if (Object.prototype.toString.call(obj[key]) === "[object Array]") {
                return obj[key]
                    .map((v) => {
                        return key + "=" + prepareQSVal(v);
                    })
                    .join("&");
            }
            return key + "=" + prepareQSVal(obj[key]);
        })
        .join("&");
}

export function buildGetParamsFromApiData({ apiData, prefix, filterEmptyStrings }) {
    const getParams = {};
    for (const [name, val] of Object.entries(apiData.forms_data[prefix].data)) {
        if (!(filterEmptyStrings && val === "")) {
            getParams[prefix + "-" + name] = val;
        }
    }
    return getParams;
}

export function parseQS(url) {
    const url_search = (url || window.location.search || "").replace(/^[^?]*\?/, "");
    return queryString.parse(url_search);
}

export function is_dict(obj) {
    return Boolean(typeof obj === "object" && !Array.isArray(obj) && obj !== null);
}

export function is_str(obj) {
    return Boolean(typeof obj === "string" || obj instanceof String);
}

export function scroll_to_top() {
    window.scrollTo({
        top: 70,
        behavior: "smooth",
    });
}

export function arrayMove(array, from, to) {
    array = array.slice();
    array.splice(to < 0 ? array.length + to : to, 0, array.splice(from, 1)[0]);
    return array;
}

// Скачивает base64 файл
export function downloadBase64File(fileContent, fileName) {
    if (fileContent && fileName) {
        const byteCharacters = Base64.atob(fileContent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
}

export function url_reverse(key, params, qs) {
    const patterns = {
        // Старые карманы на реакте ----------------------------------
        "api:pockets:list": "/dev/api/pockets/list/",
        "api:pockets:list_items": "/dev/api/pockets/<slug>/",
        "api:pockets:delete_item": "/dev/api/pockets/<slug>/<id>/",
        "api:pockets:item": "/dev/api/pockets/<slug>/<id>/",
        "api:pockets:item_locale": "/dev/api/pockets/<slug>/<id>/<locale>/",
        "api:pockets:edit_info": "/dev/api/pockets/info/<slug>/",
        "api:pockets:edit_info_locale": "/dev/api/pockets/info/<slug>/<locale>/",
        "api:pockets:vkvideo_upload_link": "/dev/api/pockets/vkvideo_upload_link/",
        // End Старые карманы на реакте ------------------------------
        "api:run_task": "/dev/api/run_task/",
        "api:suggests": "/dev/api/suggests/<suggest_type>/",
        "api:tou_accept": "/dev/api/tou_accept/",
        "api:event:newsletter": "/dev/api/event/<event_slug>/<game_id>/newsletter/<newsletter_id>/",
        "api:event:selection:item:list": "/dev/api/event/<event_slug>/<selection_id>/list_item/",
        "api:event:selection:item:add": "/dev/api/event/<event_slug>/<selection_id>/add_item/",
        "api:event:selection:item:final_list": "/dev/api/event/<event_slug>/<selection_id>/final_list/",
        "api:event:selection:item:generate_serials": "/dev/api/event/<event_slug>/generate_serials/",
        "api:event:selection:item:send_serials": "/dev/api/event/<event_slug>/send_serials/",
        "api:event:send_newsletter": "/dev/api/event/<event_slug>/send_newsletter/",
        "api:event:send_newsletter:edit": "/dev/api/event/<event_slug>/send_newsletter/<newsletter_id>/",
        "api:event:newsletter_history": "/dev/api/event/<event_slug>/newsletter_history/",
        "api:event:newsletter_history:item": "/dev/api/event/<event_slug>/newsletter_history/<newsletter_id>",
        "api:event:selection:item:edit": "/dev/api/event/<event_slug>/<selection_slug>/<item_id>/edit_item/",
        "api:event:selection:item:loc:edit": "/dev/api/event/<event_slug>/<selection_slug>/<item_id>/<loc>/edit_item/",
        "api:event_items:list": "/dev/api/event/<event_slug>/",
        "api:event:statistics": "/dev/api/event/<event_slug>/<selection_id>/statistics/",
        "api:landing:block_list": "/dev/api/landing/<selection_id>/",
        "api:landing:add_games": "/dev/api/landing/<selection_id>/add_games/",
        "api:landing:block:games": "/dev/api/landing/<selection_id>/<block_id>/games/",
        "api:landing:block:settings": "/dev/api/landing/<selection_id>/<block_id>/settings/",
        "api:landing:block:settings:item": "/dev/api/landing/<selection_id>/<block_id>/settings/<id>/",
        "api:landing:block:edit_game": "/dev/api/landing/<selection_id>/<block_id>/game/<selection_item_id>",
        "api:event:list": "/dev/api/event/",
        "api:schema_configure:edit": "/dev/api/event/schema_configure/<schema_id>/",
        "api:schema_configure": "/dev/api/event/schema_configure/",
        "api:schema:list": "/dev/api/event/schema/",
        "api:event_group:list": "/dev/api/event/pocket_group/",
        "api:schema_item:list": "/dev/api/event/schema_item/",
        "api:get_vkvideo_data": "/dev/api/get_vkvideo_data/",
        "api:common_info": "/dev/api/common_info/",
        "api:actionlog:list": "/dev/api/actionlog/<kind>/",
        "api:actionlog:item": "/dev/api/actionlog/<kind>/<log_id>/",
        "api:account:list": "/dev/api/account/",
        "api:account:edit": "/dev/api/account/<acc_id>/",
        "api:account:perm_delete": "/dev/api/account/<acc_id>/<entity_type>/<entity_id>/",
        "api:dashboard:list": "/dev/api/dashboard/<dash_type>/",
        "api:dashboard:developer:get": "/dev/api/dashboard/developers/<developer_id>/",
        "api:dashboard:game:get": "/dev/api/dashboard/games/<game_id>/",
        "api:dashboard:archive": "/dev/api/dashboard/archive/",
        "api:developer:account:list": "/dev/api/developer/<developer_id>/accounts/",
        "api:developer:list": "/dev/api/developer/",
        "api:developer:choose": "/dev/api/developer/choose/",
        "api:developer:app_form": "/dev/api/developer/<developer_id>/app_form/",
        "api:developer:common_info": "/dev/api/developer/<developer_id>/common_info/",
        "api:developer:reject": "/dev/api/developer/reject/",
        "api:developer:register": "/dev/api/developer/register/",
        "api:developer:send_letter": "/dev/api/developer/<developer_id>/send_letter/",
        "api:developer:statistics:orders": "/dev/api/developer/<developer_id>/statistics/orders/<kind>/",
        "api:developer:statistics:payments": "/dev/api/developer/<developer_id>/statistics/payments/<kind>/",
        "api:developer:statistics:vkplay_dmr": "/dev/api/developer/<developer_id>/statistics/vkplay_dmr/<kind>/",
        "api:developer:statistics:act": "/dev/api/developer/<developer_id>/statistics/act/<kind>/",
        "api:developer:statistics:documents": "/dev/api/developer/<developer_id>/statistics/documents/",
        "api:developer:statistics:documents:upload": "/dev/api/developer/<developer_id>/statistics/documents/upload/",
        "api:developer:statistics:documents_report": "/dev/api/developer/<developer_id>/statistics/documents_report/",
        "api:developer:mytracker": "/dev/api/developer/<developer_id>/mytracker/",
        "api:developer:contact_info": "/dev/api/developer/<developer_id>/contact_info/",
        "api:developer:payment_info": "/dev/api/developer/<developer_id>/payment_info/<tab>/",
        "api:developer:payment_info_actions": "/dev/api/developer/<developer_id>/payment_info_actions/",
        "api:developer:technical_info": "/dev/api/developer/<developer_id>/technical_info/",
        "api:developer:moderator_comments": "/dev/api/developer/<developer_id>/moderator_comments/",
        "api:developer:orders": "/dev/api/developer/<developer_id>/orders/",
        "api:developer:counterparty_docs": "/dev/api/developer/<developer_id>/counterparty_docs/",
        "api:developer:counterparty_docs:add": "/dev/api/developer/<developer_id>/counterparty_docs/add/",
        "api:developer:publisher:list": "/dev/api/developer/<developer_id>/publishers/",
        "api:developer:publisher:add": "/dev/api/developer/<developer_id>/publishers/add/",
        "api:developer:gamedev:list": "/dev/api/developer/<developer_id>/gamedevs/",
        "api:developer:gamedev:add": "/dev/api/developer/<developer_id>/gamedevs/add/",
        "api:developer:projects_transfer": "/dev/api/developer/<developer_id>/projects_transfer/<kind>/",
        "api:developer:projects_transfer:upload": "/dev/api/developer/<developer_id>/projects_transfer/upload/",
        "api:admin:developers_email": "/dev/api/developers_email/",
        "api:moderation:game:list": "/dev/api/moderation/game/",
        "api:moderation:game:edit": "/dev/api/moderation/game/<game_id>/",
        "api:moderation:featuring:edit": "/dev/api/moderation/featuring/<kind>/<featuring_id>/",
        "api:moderation:featuring:list": "/dev/api/moderation/featuring/<kind>/",
        "api:moderation:featuring:delete": "/dev/api/moderation/featuring/<kind>/<featuring_id>/",
        "api:moderation:event:list": "/dev/api/moderation/event/",
        "api:moderation:event:game": "/dev/api/moderation/event/<event_slug>/<game_id>/",
        "api:moderation:project_transfer:list": "/dev/api/moderation/project_transfer/<kind>/",
        "api:moderation:project_transfer:moderate": "/dev/api/moderation/project_transfer/moderate/<transfer_id>/",
        "api:moderation:pr:list": "/dev/api/moderation/pr/",
        "api:moderation:pr:developer": "/dev/api/moderation/pr/<developer_id>/",
        "api:moderation:market_serial:list": "/dev/api/moderation/market_serials/",
        "api:moderation:market_serial:edit": "/dev/api/moderation/market_serials/<game_id>/",
        "api:moderation:documents:list": "/dev/api/moderation/documents/",
        "api:moderation:documents:edit": "/dev/api/moderation/documents/<dev_id>/",
        "api:delete_chat_id": "/dev/api/chat/delete_chat_id/",
        "api:gamecenter:recreate_catalog": "/dev/api/gamecenter/recreate_catalog/",
        "api:gamecenter:gc_resync": "/dev/api/gamecenter/gc_resync/",
        "api:gamecenter:steam_dll_check": "/dev/api/gamecenter/steam_dll_check/",
        "api:gamecenter:access_test_astrum:list": "/dev/api/gamecenter/access_test_astrum/",
        "api:gamecenter:access_test_astrum:edit": "/dev/api/gamecenter/access_test_astrum/<access_id>/",
        "api:gamecenter:access_admin:list": "/dev/api/gamecenter/access_admin/",
        "api:gamecenter:access_admin:edit": "/dev/api/gamecenter/access_admin/<access_id>/",
        "api:game:common_info": "/dev/api/game/<game_id>/common_info/",
        "api:game:list": "/dev/api/game/list/",
        "api:game:admin": "/dev/api/game/<game_id>/admin/",
        "api:game:admin_actions": "/dev/api/game/<game_id>/admin_actions/",
        "api:game:account:list": "/dev/api/game/<game_id>/accounts/",
        "api:game:apk_upload": "/dev/api/game/<game_id>/apk_upload/",
        "api:game:achievement:delete": "/dev/api/game/<game_id>/achievements/<achievement_id>/",
        "api:game:achievement:edit": "/dev/api/game/<game_id>/achievements/<achievement_id>/",
        "api:game:achievement:list": "/dev/api/game/<game_id>/achievements/",
        "api:game:achievement:addmulti": "/dev/api/game/<game_id>/achievements/addmulti/",
        "api:game:misc_actions": "/dev/api/game/<game_id>/misc_actions/",
        "api:game:campaign:edit": "/dev/api/game/<game_id>/campaign/<campaign_id>/",
        "api:game:create": "/dev/api/game/create/<developer_id>/",
        "api:game:community:post:list": "/dev/api/game/<game_id>/community/posts/",
        "api:game:community:post:edit": "/dev/api/game/<game_id>/community/posts/<post_id>/",
        "api:game:betatester:list": "/dev/api/game/<game_id>/betatester/",
        "api:game:betatester:upload_list": "/dev/api/game/<game_id>/betatester/upload_list/",
        "api:game:betatester:upload_list:delete": "/dev/api/game/<game_id>/betatester/upload_list/<item_id>/",
        "api:game:betatester:upload": "/dev/api/game/<game_id>/betatester/upload/<action>/",
        "api:game:betatester:upload_delete": "/dev/api/game/<game_id>/betatester/upload/<upload_id>/",
        "api:game:stream": "/dev/api/game/<game_id>/stream/",
        "api:game:featuring_launch": "/dev/api/game/<game_id>/featuring/launch/",
        "api:game:featuring_promo:delete": "/dev/api/game/<game_id>/featuring/promo/<promo_id>/",
        "api:game:featuring_promo:edit": "/dev/api/game/<game_id>/featuring/promo/<promo_id>/",
        "api:game:featuring_promo:list": "/dev/api/game/<game_id>/featuring/promo/",
        "api:game:gamebox": "/dev/api/game/<game_id>/gamebox/",
        "api:game:inc_iframe_version": "/dev/api/game/<game_id>/inc_iframe_version/",
        "api:game:locale:edit": "/dev/api/game/<game_id>/locales/<locl>/",
        "api:game:ai": "/dev/api/game/<game_id>/ai/",
        "api:game:microreview:edit": "/dev/api/game/<game_id>/microreviews/<microreview_id>/",
        "api:game:microreview:list": "/dev/api/game/<game_id>/microreviews/",
        "api:game:microreview:stats": "/dev/api/game/<game_id>/microreviews/stats/",
        "api:game:microreview:subscription_reviews": "/dev/api/game/<game_id>/microreviews/subscription_reviews/",
        "api:game:relationship": "/dev/api/game/<game_id>/relationship/",
        "api:game:bundle:add": "/dev/api/game/<game_id>/bundle/add/",
        "api:game:bundle_item:list": "/dev/api/game/<game_id>/bundle_items/",
        "api:game:dlc:add": "/dev/api/game/<game_id>/dlc/add/",
        "api:game:demo:add": "/dev/api/game/<game_id>/demo/add/",
        "api:game:playtest:add": "/dev/api/game/<game_id>/playtest/add/",
        "api:game:playtest:users": "/dev/api/game/<game_id>/playtest/users/",
        "api:game:playtest:users:manage": "/dev/api/game/<game_id>/playtest/users/<act>/",
        "api:game:highlights:vkvideo_upload_link": "/dev/api/game/<game_id>/highlights/<locl>/vkvideo_upload_link/",
        "api:game:highlights:add": "/dev/api/game/<game_id>/highlights/<locl>/add/",
        "api:game:highlights:list": "/dev/api/game/<game_id>/highlights/<locl>/",
        "api:game:highlights:edit": "/dev/api/game/<game_id>/highlights/<locl>/<id>/",
        "api:game:highlights:delete": "/dev/api/game/<game_id>/highlights/<locl>/<id>/",
        "api:game:highlights:bulk_delete": "/dev/api/game/<game_id>/highlights/bulk/delete/",
        "api:game:highlights:draft:add": "/dev/api/game/<game_id>/highlights/<locl>/draft/add/",
        "api:game:highlights:draft:edit": "/dev/api/game/<game_id>/highlights/<locl>/draft/<id>/",
        "api:game:steam_gift_prices": "/dev/api/game/<game_id>/steam_gift_prices/",
        "api:game:prices": "/dev/api/game/<game_id>/prices/",
        "api:game:serials:ban": "/dev/api/game/<game_id>/serials/ban/",
        "api:game:serials:batch_list": "/dev/api/game/<game_id>/serials/batch_list/",
        "api:game:serials:generation": "/dev/api/game/<game_id>/serials/generation/",
        "api:game:codes": "/dev/api/game/<game_id>/codes/",
        "api:game:codes:edit": "/dev/api/game/<game_id>/codes/<code_id>/",
        "api:game:s3mirrors": "/dev/api/game/<game_id>/s3mirrors/",
        "api:game:countries": "/dev/api/game/<game_id>/countries/",
        "api:game:publication:developer": "/dev/api/game/<game_id>/publication/developer/",
        "api:game:publication:moderator": "/dev/api/game/<game_id>/publication/moderator/",
        "api:game:distribution": "/dev/api/game/<game_id>/distribution/",
        "api:game:save_lpifrurls": "/dev/api/game/<game_id>/save_lpifrurls/",
        "api:game:statistics:orders": "/dev/api/game/<game_id>/statistics/orders/",
        "api:game:statistics:payments": "/dev/api/game/<game_id>/statistics/payments/",
        "api:game:statistics:refunds": "/dev/api/game/<game_id>/statistics/refunds/",
        "api:game:statistics:sunduki": "/dev/api/game/<game_id>/statistics/sunduki/",
        "api:game:statistics:wishlist": "/dev/api/game/<game_id>/statistics/wishlist/",
        "api:game:statistics:mytracker": "/dev/api/game/<game_id>/statistics/mytracker/",
        "api:game:statistics:general": "/dev/api/game/<game_id>/statistics/general/",
        "api:game:statistics:onlines": "/dev/api/game/<game_id>/statistics/onlines/",
        "api:game:system": "/dev/api/game/<game_id>/system/",
        "api:game:unpublish": "/dev/api/game/<game_id>/unpublish/",
        "api:game:moderator_comments": "/dev/api/game/<game_id>/moderator_comments/",
        "api:game:gameitems": "/dev/api/game/<game_id>/gameitems/",
        "api:game:gameitems:versions": "/dev/api/game/<game_id>/gameitems/<itemdefid>/",
        "api:market:buka:list": "/dev/api/market/buka/",
        "api:market:enaza:list": "/dev/api/market/enaza/",
        "api:market:sales:list": "/dev/api/market/sales/",
        "api:market:order:list": "/dev/api/market/order/",
        "api:market:order:check_refund_info": "/dev/api/market/order/check_refund_info/",
        "api:market:foreign_invoice:list": "/dev/api/market/foreign_invoice/",
        "api:market:steam_gift_order:list": "/dev/api/market/steam_gift_order/",
        "api:market:error:list": "/dev/api/market/error/",
        "api:market:payment:list": "/dev/api/market/payment/",
        "api:market:payment:refund:list": "/dev/api/market/payment_refund/",
        "api:market:promocode:list": "/dev/api/market/promocode/",
        "api:market:promocode:generation": "/dev/api/market/promocode/generation/",
        "api:market:promocode:log": "/dev/api/market/promocode/log/",
        "api:market:promocode:ban": "/dev/api/market/promocode/ban/",
        "api:market:platform:delete": "/dev/api/market/platform/<platform_id>/",
        "api:market:platform:edit": "/dev/api/market/platform/<platform_id>/",
        "api:market:platform:list": "/dev/api/market/platform/",
        "api:market:serial:list": "/dev/api/market/serial/",
        "api:market:ckassa_blacklist:list": "/dev/api/market/ckassa_blacklist/",
        "api:market:ckassa_blacklist:edit": "/dev/api/market/ckassa_blacklist/<blacklist_id>/",
        "api:market:merchant_set:list": "/dev/api/market/merchant_set/",
        "api:upcoming_releases": "/dev/api/upcoming_releases/",
        "api:notice:close": "/dev/api/notice/<notice_id>/close/",
        "api:notice:delete": "/dev/api/notice/<notice_id>/",
        "api:notice:edit": "/dev/api/notice/<notice_id>/",
        "api:notice:list": "/dev/api/notice/",
        "api:microreview:list": "/dev/api/microreview/",
        "api:microreview:complaints": "/dev/api/microreview/complaints/",
        "api:publisher:list": "/dev/api/publisher/",
        "api:publisher:edit": "/dev/api/publisher/<publisher_id>/",
        "api:publisher:developers": "/dev/api/publisher/<publisher_id>/developers/",
        "api:publisher:games": "/dev/api/publisher/<publisher_id>/games/",
        "api:tag:countries": "/dev/api/tags/<tag_id>/countries/",
        "api:tag:delete": "/dev/api/tags/<tag_id>/",
        "api:tag:edit": "/dev/api/tags/<tag_id>/",
        "api:tag:games": "/dev/api/tags/<tag_id>/games/",
        "api:tag:list": "/dev/api/tags/",
        "api:finstatements:documents": "/dev/api/finstatements/documents/",
        "api:finstatements:documents:upload": "/dev/api/finstatements/documents/upload/",
        "api:finstatements:month": "/dev/api/finstatements/month/",
        "api:finstatements:dmr": "/dev/api/finstatements/dmr/",
        "api:finstatements:royalty": "/dev/api/finstatements/royalty/",
        "api:finstatements:report_cloud:list": "/dev/api/finstatements/report_cloud/",
        "api:finstatements:report_cloud:edit": "/dev/api/finstatements/report_cloud/<report_id>/",
        "api:finstatements:report:merchants": "/dev/api/finstatements/report/merchants",
        "api:finstatements:report:status": "/dev/api/finstatements/report/status",
        "api:finstatements:report:info": "/dev/api/finstatements/report/info",
        "api:finstatements:report:download": "/dev/api/finstatements/report/download",
        "api:finstatements:report:post_actions": "/dev/api/finstatements/report/post_actions",
        "api:finstatements:merchant:list": "/dev/api/finstatements/report/merchants/list/",
        "api:finstatements:merchant:create": "/dev/api/finstatements/report/merchants/create/",
        "api:finstatements:merchant:update": "/dev/api/finstatements/report/merchants/update/",
        "api:finstatements:merchant:delete": "/dev/api/finstatements/report/merchants/delete/",
        "api:finstatements:developer_receipts": "/dev/api/finstatements/developer_receipts/",
        "api:finstatements:upload": "/dev/api/finstatements/upload/",
        "api:finstatements:payment_registry": "/dev/api/finstatements/payment_registry/",
        "api:statements:games_report": "/dev/api/statements/games_report/",
        "api:statements:developers_report": "/dev/api/statements/developers_report/",
        // api для конструктора лендингов
        "api:landing_ct:landings": "/dev/api/landing_ct/landings/",
        "api:landing_ct:landing_add": "/dev/api/landing_ct/landing/add/",
        "api:landing_ct:landing_blocks": "/dev/api/landing_ct/<slug>/blocks/",
        "api:landing_ct:landing_edit_delete": "/dev/api/landing_ct/landing/<pk>/",
        "api:landing_ct:landing_blocks:add": "/dev/api/landing_ct/<slug>/block/add/",
        "api:landing_ct:landing_blocks:crud": "/dev/api/landing_ct/<slug>/block/<item_id>/",
        "api:landing_ct:block_items": "/dev/api/landing_ct/landing_block/<block_id>/items/",
        "api:landing_ct:block_items:crud": "/dev/api/landing_ct/landing_block/<block_id>/items/<item_id>/",

        "admin:account:list": "/dev/admin/account/",
        "admin:account:edit": "/dev/admin/account/<acc_id>/",
        "admin:actionlog:list": "/dev/admin/actionlog/<kind>/",
        "admin:actionlog:item": "/dev/admin/actionlog/<kind>/<log_id>/",
        "admin:developers_email": "/dev/admin/developers_email/",
        "admin:gamecenter:access_test_astrum:list": "/dev/admin/gamecenter/access_test_astrum/",
        "admin:gamecenter:access_test_astrum:edit": "/dev/admin/gamecenter/access_test_astrum/<access_id>/",
        "admin:gamecenter:access_admin:list": "/dev/admin/gamecenter/access_admin/",
        "admin:gamecenter:access_admin:edit": "/dev/admin/gamecenter/access_admin/<access_id>/",
        "admin:notice:edit": "/dev/admin/notice/<notice_id>/",
        "admin:notice:list": "/dev/admin/notice/",
        "admin:publisher:list": "/dev/admin/publisher/",
        "admin:upcoming_releases:list": "/dev/admin/upcoming_releases/",
        "admin:publisher:edit": "/dev/admin/publisher/<publisher_id>/",
        "admin:publisher:developers": "/dev/admin/publisher/<publisher_id>/developers/",
        "admin:publisher:games": "/dev/admin/publisher/<publisher_id>/games/",
        "admin:tag:countries": "/dev/admin/tags/<tag_id>/countries/",
        "admin:tag:edit": "/dev/admin/tags/<tag_id>/",
        "admin:tag:games": "/dev/admin/tags/<tag_id>/games/",
        "admin:tag:list": "/dev/admin/tags/",
        "admin:pockets:list": "/dev/admin/pockets/",
        "admin:pockets:items": "/dev/admin/pockets/<slug>/",
        "admin:pockets:item": "/dev/admin/pockets/<slug>/<id>/",
        "admin:pockets:item_locale": "/dev/admin/pockets/<slug>/<id>/<locale>/",
        "admin:pockets:copy": "/dev/admin/pockets/<slug>/copy/<id>/",
        "admin:pockets:edit_info": "/dev/admin/pockets/info/<slug>/",
        "admin:pockets:edit_info_locale": "/dev/admin/pockets/info/<slug>/<locale>/",
        "admin:event:list": "/dev/admin/event/",
        "admin:event:items": "/dev/admin/event/<slug>/",
        "admin:event:landing": "/dev/admin/event/<slug>/<selection_id>/landing/",
        "admin:event:landing_block:games": "/dev/admin/event/<slug>/<selection_id>/landing/<block_id>/games/",
        "admin:event:landing_block:settings": "/dev/admin/event/<slug>/<selection_id>/landing/<block_id>/settings/",
        "admin:event:landing_block:settings:item":
            "/dev/admin/event/<slug>/<selection_id>/landing/<block_id>/settings/<id>/",
        "admin:event:landing_block:edit_game":
            "/dev/admin/event/<slug>/<selection_id>/landing/<block_id>/game/<selection_item_id>",
        "admin:event:landing_add_games": "/dev/admin/event/<slug>/<selection_id>/landing/add_games/",
        "admin:event:settings": "/dev/admin/event/<slug>/settings/",
        "admin:event:generate_serials": "/dev/admin/event/<slug>/generate_serials/",
        "admin:event:send_serials": "/dev/admin/event/<slug>/send_serials/",
        "admin:event:selection:items:add": "/dev/admin/event/<event_slug>/<selection_id>/add/",
        "admin:event:send_newsletter": "/dev/admin/event/<event_slug>/send_newsletter/",
        "admin:event:send_newsletter:edit": "/dev/admin/event/<event_slug>/send_newsletter/<newsletter_id>/",
        "admin:event:newsletter_history:list": "/dev/admin/event/<event_slug>/newsletter_history/",
        "admin:event:newsletter_history:item": "/dev/admin/event/<event_slug>/newsletter_history/<newsletter_id>",
        "admin:event:selection:items:edit": "/dev/admin/event/<event_slug>/<selection_slug>/<item_id>/edit/",
        "admin:event:selection:items:loc:edit": "/dev/admin/event/<event_slug>/<selection_slug>/<item_id>/<loc>/edit/",
        "admin:event:event_group:list": "/dev/admin/event/pocket_group/",
        "admin:event:schemes:list": "/dev/admin/event/schemes/",
        "admin:event:field_constructor:list": "/dev/admin/event/field_constructor/",
        "admin:event:schemes:configuration": "/dev/admin/event/schemes/configuration/",
        "admin:event:schemes:configuration:edit": "/dev/admin/event/schemes/configuration/<scheme_id>/",
        // Конструктор Лендингов
        "admin:landing_ct:landings": "/dev/admin/landing_ct/",
        "admin:landing_ct:add_landing": "/dev/admin/landing_ct/add/",
        "admin:landing_ct:landing_items": "/dev/admin/landing_ct/<slug>/",
        "admin:landing_ct:edit_landing": "/dev/admin/landing_ct/<item_id>/edit/",
        "admin:landing_ct:block_items": "/dev/admin/landing_ct/<slug>/<block_id>/",
        "admin:landing_ct:block_settings": "/dev/admin/landing_ct/<slug>/<block_id>/settings/",
        "admin:landing_ct:block_item_edit": "/dev/admin/landing_ct/<slug>/<block_id>/<item_id>/",
        // Модерация игр
        "admin:moderation:project_transfer:list": "/dev/admin/moderation/project_transfer/<kind>/",
        "admin:moderation:project_transfer:edit": "/dev/admin/moderation/project_transfer/<request_id>/",
        "admin:moderation:market_serial:list": "/dev/admin/moderation/market_serials/",
        "admin:moderation:market_serial:edit": "/dev/admin/moderation/market_serials/<game_id>/",
        "admin:moderation:documents:list": "/dev/admin/moderation/documents/",
        "admin:moderation:documents:edit": "/dev/admin/moderation/documents/<dev_id>/",
        "admin:moderation:game:list": "/dev/admin/moderation/game/",
        "admin:moderation:game:edit": "/dev/admin/moderation/game/<game_id>/",
        "admin:moderation:pr:list": "/dev/admin/moderation/pr/",
        "admin:moderation:pr:developer": "/dev/admin/moderation/pr/<developer_id>/",
        "admin:moderation:event:list": "/dev/admin/moderation/event/",
        "admin:moderation:event:game": "/dev/admin/moderation/event/<event_slug>/<game_id>/",
        "admin:moderation:featuring:edit": "/dev/admin/moderation/featuring/<kind>/<featuring_id>/",
        "admin:moderation:featuring:list": "/dev/admin/moderation/featuring/<kind>/",
        "moderate:comments": "/dev/moderate/comments/",
        "moderate:community": "/dev/moderate/community/",
        "moderate:chat": "/dev/moderate/chat/",
        "moderate:profile_posts": "/dev/moderate/profile_posts/",
        "moderate:microreview:list": "/dev/moderate/microreviews/",
        "moderate:microreview:complaints": "/dev/moderate/microreviews/complaints/",
        "devs:edit": "/dev/developers/<dev_id>/",
        "devs:accounts": "/dev/developers/<dev_id>/accounts/",
        "devs:counterparty_docs": "/dev/developers/<dev_id>/counterparty_docs/",
        "devs:counterparty_docs:add": "/dev/developers/<dev_id>/counterparty_docs/add/",
        "devs:dashboard": "/dev/dashboard/<dash_type>/",
        "devs:payment_info": "/dev/developers/<dev_id>/payment_info/<tab>/",
        "devs:mytracker": "/dev/developers/<dev_id>/mytracker/",
        "devs:publishers": "/dev/developers/<dev_id>/publishers/",
        "devs:publishers:add": "/dev/developers/<dev_id>/publishers/add/",
        "devs:gamedevs": "/dev/developers/<dev_id>/gamedevs/",
        "devs:gamedevs:add": "/dev/developers/<dev_id>/gamedevs/add/",
        "devs:list": "/dev/developers/",
        "devs:projects_transfer": "/dev/developers/<dev_id>/projects_transfer/<kind>/",
        "devs:reject": "/dev/developers/reject/",
        "devs:register": "/dev/developers/register/",
        tou_accept: "/dev/tou_accept/",
        "finstatements:documents": "/dev/admin/finstatements/documents/",
        "finstatements:month": "/dev/admin/finstatements/month/",
        "finstatements:payments_control": "/dev/admin/finstatements/payments_control/",
        "finstatements:dmr": "/dev/admin/finstatements/dmr/",
        "finstatements:report_cloud:list": "/dev/admin/finstatements/report_cloud/",
        "finstatements:report_cloud:edit": "/dev/admin/finstatements/report_cloud/<report_id>/",
        "finstatements:royalty": "/dev/admin/finstatements/royalty/",
        "finstatements:developer_receipts": "/dev/admin/finstatements/developer_receipts/",
        "finstatements:merchants": "/dev/admin/finstatements/merchants/",
        "finstatements:report:merchants": "/dev/admin/finstatements/report/merchants",
        "finstatements:report:sync": "/dev/admin/finstatements/report/sync",
        "finstatements:report:status": "/dev/admin/finstatements/report/status",
        "finstatements:report:info": "/dev/admin/finstatements/report/info",
        "finstatements:report:make": "/dev/admin/finstatements/report/make",
        "finstatements:report:cleanup": "/dev/admin/finstatements/report/cleanup",
        "finstatements:report:download": "/dev/admin/finstatements/report/download",
        "finstatements:merchants:list": "/dev/admin/finstatements/merchants/list/",
        "finstatements:merchant:create": "/dev/admin/finstatements/merchants/create/",
        "finstatements:merchant:update": "/dev/admin/finstatements/merchants/update/",
        "finstatements:merchant:delete": "/dev/admin/finstatements/merchants/delete/",

        "statements:games_report": "/dev/admin/statements/games_report/",
        "statements:developers_report": "/dev/admin/statements/developers_report/",
        "games:account:list": "/dev/games/<game_id>/accounts/",
        "games:achievement:add": "/dev/games/<game_id>/achievements/add/",
        "games:achievement:addmulti": "/dev/games/<game_id>/achievements/addmulti/",
        "games:achievement:edit": "/dev/games/<game_id>/achievements/<achievement_id>/<locale>/",
        "games:achievement:list": "/dev/games/<game_id>/achievements/",
        "games:admin": "/dev/games/<game_id>/admin/",
        "games:apk_upload": "/dev/games/<game_id>/apk_upload/",
        "games:betatester:list": "/dev/games/<game_id>/betatesters/",
        "games:betatester:upload_list": "/dev/games/<game_id>/betatesters/upload_list/",
        "games:betatester:add": "/dev/games/<game_id>/betatesters/add/",
        "games:betatester:del": "/dev/games/<game_id>/betatesters/del/",
        "games:stream": "/dev/games/<game_id>/stream/",
        "games:campaign:edit": "/dev/games/<game_id>/campaigns/<campaign_id>/",
        "games:code:list": "/dev/games/<game_id>/codes/",
        "games:code:edit": "/dev/games/<game_id>/codes/<code_id>/",
        "games:moderate:comments": "/dev/moderate/comments/?play_game_id=<game_id>",
        "games:community": "/dev/games/<game_id>/community/",
        "games:community:edit": "/dev/games/<game_id>/community/<post_id>/",
        "games:countries": "/dev/games/<game_id>/countries/",
        "games:create": "/dev/games/create/<developer_id>/",
        "games:create:devchoose": "/dev/games/create/developer_choose/",
        "games:distributions": "/dev/games/<game_id>/distributions/",
        "games:page": "/dev/games/<game_id>/",
        "games:featuring:launch": "/dev/games/<game_id>/featuring/launch/",
        "games:featuring:promo:edit": "/dev/games/<game_id>/featuring/promo/<promo_id>/",
        "games:featuring:promo:list": "/dev/games/<game_id>/featuring/promo/",
        "games:highlights:add": "/dev/games/<game_id>/highlights/<locale>/add/",
        "games:highlights:addmulti": "/dev/games/<game_id>/highlights/<locale>/addmulti/",
        "games:highlights:edit": "/dev/games/<game_id>/highlights/<locale>/<id>/",
        "games:highlights:draft:add": "/dev/games/<game_id>/highlights/<locale>/draft/add/",
        "games:highlights:draft:addmulti": "/dev/games/<game_id>/highlights/<locale>/draft/addmulti/",
        "games:highlights:draft:edit": "/dev/games/<game_id>/highlights/<locale>/draft/<id>/",
        "games:gameitem:list": "/dev/games/<game_id>/gameitems/",
        "games:gameitem:versions": "/dev/games/<game_id>/gameitems/<itemdefid>/",
        "games:list": "/dev/games/",
        "games:locale:edit": "/dev/games/<game_id>/locales/<locale>/",
        "games:microreview:edit": "/dev/games/<game_id>/microreviews/<microreview_id>/",
        "games:microreview:list": "/dev/games/<game_id>/microreviews/",
        "games:microreview:stats": "/dev/games/<game_id>/microreviews/stats/",
        "games:microreview:subscription_reviews": "/dev/games/<game_id>/microreviews/subscription_reviews/",
        "games:prices": "/dev/games/<game_id>/prices/",
        "games:relationship": "/dev/games/<game_id>/relationship/",
        "games:relationship:add": "/dev/games/<game_id>/relationship/<rel_type>/add/",
        "games:s3mirrors": "/dev/games/<game_id>/s3mirrors/",
        "games:serials": "/dev/games/<game_id>/serials/",
        "games:serials:ban": "/dev/games/<game_id>/serials/ban/",
        "games:serials:generation": "/dev/games/<game_id>/serials/generation/",
        "games:statistics:orders": "/dev/games/<game_id>/statistics/orders/",
        "games:statistics:payments": "/dev/games/<game_id>/statistics/payments/",
        "games:statistics:wishlist": "/dev/games/<game_id>/statistics/wishlist/",
        "games:system": "/dev/games/<game_id>/system/",
        "games:unpublish": "/dev/games/<game_id>/unpublish/",
        "games:moderator_comments": "/dev/games/<game_id>/moderator_comments/",
        "games:event:newsletter": "/dev/games/<game_id>/featuring/event/<event_slug>/<newsletter_id>/",
        "games:playtest": "/dev/games/<game_id>/playtest/",
        "games:playtest:users_add": "/dev/games/<game_id>/playtest/users_add/",
        "games:playtest:users_del": "/dev/games/<game_id>/playtest/users_del/",
        "market:buka": "/dev/market/buka/",
        "market:enaza": "/dev/market/enaza/",
        "market:order:list": "/dev/market/orders/",
        "market:foreign_invoice:list": "/dev/market/foreign_invoice/",
        "market:steam_gift_order:list": "/dev/market/steam_gift_order/",
        "market:ckassa_blacklist:list": "/dev/market/ckassa_blacklist/",
        "market:ckassa_blacklist:edit": "/dev/market/ckassa_blacklist/<blacklist_id>/",
        "market:error:list": "/dev/market/errors/",
        "market:platform:edit": "/dev/market/platforms/<platform_id>/",
        "market:platform:list": "/dev/market/platforms/",
        "market:serial:list": "/dev/market/serials/",
        "market:promocode:list": "/dev/market/promocode/",
        "market:promocode:log": "/dev/market/promocode/log/",
        "market:promocode:ban": "/dev/market/promocode/ban/",
        "market:promocode:generation": "/dev/market/promocode/generation/",
        "market:sale:list": "/dev/market/sales/",
        "partner:payment:list": "/dev/market/payments/",
        "market:merchant_set:list": "/dev/market/merchant_set/",
        "pocket:item:edit": "/dev/admin/pockets/<slug>/<item_id>/",
        "pocket:list": "/dev/admin/pockets/",
    };

    let path = patterns[key] || "";
    if (!path) {
        console.error(`url_reverse\nkey ${key} not found`);
    }
    for (const [name, val] of Object.entries(params || {})) {
        path = path.replace(`<${name}>`, val);
    }
    if (qs) {
        path += "?" + buildQS(qs);
    }
    return path;
}

/**
 * @param {File} file
 * @returns {Promise<{width: number, height: number, duration: number, audio: boolean}>}
 */
export const getVideoInfo = (file) =>
    new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.onloadedmetadata = async () => {
            URL.revokeObjectURL(url);

            resolve({
                width: video.videoWidth,
                height: video.videoHeight,
                duration: video.duration,
            });
        };
        video.src = url;
        video.load();
    });

export const setProperty = (obj, path, value) => {
    const [head, ...rest] = path.split(".");

    return {
        ...obj,
        [head]: rest.length ? setProperty(obj[head], rest.join("."), value) : value,
    };
};

export const parseMysqlDatetime = (s) => {
    if (!s) {
        return;
    }
    const date = s.split(/[- :]/);
    date[1]--;
    return new Date(...date);
};

export function validateYouTubeUrl(url) {
    if (url) {
        const p =
            /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
        if (url.match(p)) {
            return url.match(p)[1];
        }
    }
    return false;
}

export const humanTimer = (date0, date1) => {
    if (!date0 || !date1) {
        return;
    }
    const dt1 = new Date(date1);
    const diffMs = dt1 - new Date(date0);
    const diffDays = Math.floor(diffMs / 86400000); // days
    const diffHrs = Math.floor((diffMs % 86400000) / 3600000); // hours
    const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000); // minutes
    return (
        (diffDays ? diffDays + "d " : "") + (diffHrs ? diffHrs + "h " : "") + (diffMins ? diffMins + "m" : "") || "<1m"
    );
};

export const money_fmt = (smth) => {
    return smth.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ");
};

export const getEventSubmitter = (ev) => {
    // нажатая кнопка
    let node = null;
    if (ev.nativeEvent.submitter?.tagName === "BUTTON") {
        // если сабмит формы происходит по кнопке type="submit", на которой не висит onClick
        node = ev.nativeEvent.submitter;
    } else {
        node = ev.currentTarget;
    }
    return { tn: node.name, tv: node.value };
};

export const calcPriceFromDiscount = (currency, fullCost, discount) => {
    // полная копия функции с бека calc_price_from_discount, если правим здесь то необходимо согласовать с беком
    if (discount > 100) {
        return 0.0;
    }
    if (currency === "rub") {
        return Math.floor((fullCost * (100 - discount)) / 100);
    }
    const Decimal = require("decimal.js-light");
    const D100 = new Decimal(100);
    const fullCostDecimal = new Decimal(fullCost);
    const discountDecimal = new Decimal(discount);
    const res = fullCostDecimal.mul(D100.minus(discountDecimal)).div(D100);
    return Math.trunc(parseFloat(res.toFixed(3)) * 100) / 100;
};

export const calcDiscountFromPrice = (fullCost, discountPrice) => {
    fullCost = parseFloat(fullCost);
    discountPrice = parseFloat(discountPrice);
    // полная копия функции с бека calc_discount_from_price, если правим здесь то необходимо согласовать с беком
    if (discountPrice >= fullCost || !discountPrice) {
        return 0;
    }
    const discountPercentage = (1 - discountPrice / fullCost) * 100;
    return Math.floor(discountPercentage);
};

export function ScrollToElement(element) {
    //TODO: Нужно пофиксить и убрать тайм-аут. Не работает в "Основные свойства" при переключении на вкладку
    setTimeout(() => {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);

    element.style.transition = "background-color 1s ease, box-shadow 1s ease";
    element.style.backgroundColor = "#F2F8CB";
    element.style.boxShadow = "0 0 0 5px #F2F8CB";
    setTimeout(() => {
        element.style.backgroundColor = "transparent";
        element.style.boxShadow = "none";
    }, 2500);
}

function isEmpty(obj) {
    for (const prop in obj) {
        if (Object.hasOwn(obj, prop)) {
            return false;
        }
    }

    return true;
}

export const isEmptyObject = (value) => {
    if (value == null) {
        return false;
    }

    if (typeof value !== "object") {
        return false;
    }

    const proto = Object.getPrototypeOf(value);

    if (proto !== null && proto !== Object.prototype) {
        return false;
    }

    return isEmpty(value);
};

export const Base64ToPDFBlob = (base64String) => {
    base64String ||= "";
    // Remove data URL prefix if it exists
    const base64Data = base64String.replace(/^data:([^;]+);base64,/, "");

    // Convert base64 to raw binary data
    const binaryData = atob(base64Data);

    // Create array buffer from binary data
    const arrayBuffer = new ArrayBuffer(binaryData.length);
    const uint8Array = new Uint8Array(arrayBuffer);

    // Fill array buffer with binary data
    for (let i = 0; i < binaryData.length; i++) {
        uint8Array[i] = binaryData.charCodeAt(i);
    }

    // Create blob from array buffer
    return new Blob([arrayBuffer], { type: "application/pdf" });
};

export const linkify = (text) => {
    /*
        Находит URL в тексте и оборачивает их в кликабельные ссылки
          @param {string} text - Входной текст или HTML
          @returns {string} - HTML-строка с обёрнутыми ссылками
     */

    return linkifyHtml(text, {
        target: "_blank",
        rel: "noopener noreferrer",
    });
};

export const parseAndLinkify = (text) => {
    /*
        Преобразует текст: находит URL, оборачивает их в ссылки, очищает от XSS и возвращает React-элементы
          @param {string} text - Входной текст (может содержать URL и HTML)
          @returns {JSX.Element|string} - React-элементы для рендера или пустая строка
     */

    if (!text) return "";

    const linkedText = linkify(text);
    // ADD_ATTR: DOMPurify по умолчанию вырезает target у ссылок,
    // а linkify() добавляет его (target="_blank") — сохраняем атрибут.
    return parse(DOMPurify.sanitize(linkedText, { ADD_ATTR: ["target"] }));
};

export const getAttachement = (url, opts = {}) => {
    fetch(url)
        .then((response) => {
            if (response.status !== 200) {
                alert(response.statusText);
                return;
            }
            if (response.headers.get("Content-Type") === "application/json") {
                return response.json().then((resp) => {
                    alert(resp.errmsg);
                });
            }
            return response.blob().then((blob) => {
                const contentType =
                    response.headers.get("Content-Type") ||
                    response.headers.get("content-type") ||
                    "application/octet-stream";
                const file = new Blob([blob], { type: contentType });

                // downloadUrl
                const link = document.createElement("a");
                link.href = window.URL.createObjectURL(file);

                // filename
                let filename = response.headers.get("Attachment-Name");
                const contentDisposition =
                    response.headers.get("Content-Disposition") || response.headers.get("content-disposition");
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
                    if (match && match[1]) {
                        filename = decodeURIComponent(match[1]);
                    }
                }
                if (!filename) {
                    filename = "attachment";
                }
                link.setAttribute("download", filename);

                // Append to html link element page
                document.body.appendChild(link);

                // Start download
                link.click();

                // Clean up and remove the link
                link.parentNode.removeChild(link);
            });
        })
        .finally(() => {
            if (opts.finally) opts.finally();
        });
};

export const prevMonthPeriod = () => {
    const date = new Date();
    const prevMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    return prevMonth.getFullYear().toString() + (prevMonth.getMonth() + 1).toString().padStart(2, "0");
};
